import { ConstToEnumType } from '@whitelist/wl-old-library/utils/types'
import { GetterTree, MutationTree, ActionTree } from 'vuex'
import services from '@/services/index'
import { RESPONSE_STATUS_CODES } from '@whitelist/wl-old-library/constants/serverConstants'
import {
  createNotificationsPaginationGenerator,
  NotificationsFilters,
  prioritizeModals,
  getAlertAction,
  getModalAction
} from './helpers'
import { Notification } from './helpers/types'
import { parseNotificationString } from '@/constants/notifications'
import { ActivePopupTypes } from '@whitelist/wl-old-library/components/global/types'
import _ from 'lodash'

const namespaced = true

class State {
  notifications: Notification[] = []
  isError = false
  displayedModal: Notification | null = null
}

const getters = <GetterTree<State, any>> {
  getAlerts: state => state.notifications.filter(item => item.type === 'alert' && !item.read),
  getPassport: state => state.notifications.filter(item => item.type === 'passport' && !item.read),
  getModals: state => state.notifications.filter(item => item.type === 'modal' && !item.read),
  getTopBar: state => state.notifications.filter(item => item.type === 'topBar' && !item.read),
  getAll: state => state.notifications,
  getModalsPrioritized: (state, getters) => getters.getModals
    .filter((item: Notification) => item.id !== state.displayedModal?.id)
    .sort(prioritizeModals),
  isUiBlocking: state => !!_.get(state.displayedModal, 'blockUi')
}

const actions = <ActionTree<State, any>>{
  async loadAll ({ commit, dispatch, state }): Promise<void> {
    const notifications: Notification[] = []

    const filters: NotificationsFilters = {
      order: 'created_at',
      page: 1
    }
    let errorCounter = 0

    for await (const pageResponse of createNotificationsPaginationGenerator(filters)) {
      if (pageResponse.status === RESPONSE_STATUS_CODES.OK) {
        const newNotifications = pageResponse.data.data as Notification[]
        notifications.push(...newNotifications)
        errorCounter = 0
      } else {
        errorCounter += 1
      }
      if (errorCounter >= 2) {
        dispatch('handleError')
        break
      }
    }

    commit('replaceNotifications', notifications)
    dispatch('displayAlerts', notifications)
    if (state.displayedModal === null) {
      dispatch('displayModals')
    }
  },
  async read ({ commit, dispatch }, payload: Notification['id']): Promise<void> {
    const readResponse = await services.notifications.readNotification({ notificationId: payload, payload: null })
    if (readResponse.status === RESPONSE_STATUS_CODES.OK) {
      commit('setRead', payload)
    } else {
      dispatch('handleError')
    }
  },
  async displayModals ({ dispatch, getters, commit }): Promise<void> {
    const [currentModal] = getters.getModalsPrioritized as Notification[]
    if (currentModal === undefined) return

    commit('setDisplayedModal', currentModal)

    const onBeforeClose = async () => {
      await dispatch('read', currentModal.id)
      commit('setDisplayedModal', null)
      dispatch('displayModals')
    }

    dispatch('popupModule/openPopup', {
      type: ActivePopupTypes.action,
      letDefaultClose: !currentModal.blockUi,
      title: currentModal.title === null ? '' : parseNotificationString(currentModal.title, currentModal.vars),
      content: '',
      actions: getModalAction(currentModal, async () => {
        commit('popupModule/setClosed', undefined, { root: true })
        await onBeforeClose()
      }),
      slot: {
        name: 'commonNotificationPopupChunk',
        data: {
          messages: currentModal.body.map((text: string) => parseNotificationString(text, currentModal.vars))
        }
      },
      onBeforeClose
    }, { root: true })
  },
  displayAlerts ({ dispatch }, payload: Notification[]): void {
    const alerts = payload.filter(item => item.type === 'alert' && !item.read)
    alerts.forEach(alert => {
      dispatch('alertsBus/openAlert', {
        title: alert.title === null ? '' : parseNotificationString(alert.title, alert.vars),
        type: alert.state,
        autoDelete: false,
        messages: alert.body.map(body => parseNotificationString(body, alert.vars)),
        onlyContent: alert.title === null || alert.title.length === 0,
        wordBreak: true,
        action: getAlertAction(alert),
        contentType: alert.contentType,
        onClose: alert.readable ? () => { dispatch('read', alert.id) } : undefined
      }, { root: true })
    })
  },
  handleError ({ commit }): void {
    commit('setError', true)
  }
}
const mutations = <MutationTree<State>>{
  replaceNotifications (state, payload: Notification[]) {
    state.notifications = payload
  },
  addNotifications (state, payload: Notification[]) {
    state.notifications = state.notifications.concat(payload)
  },
  setError (state, payload: boolean) {
    state.isError = payload
  },
  setRead (state, payload: Notification['id']) {
    state.notifications = state.notifications.map(item => item.id === payload ? { ...item, read: true } : item)
  },
  setDisplayedModal (state, payload: Notification | null) {
    state.displayedModal = payload
  }
}

export default {
  namespaced,
  state: new State(),
  getters,
  actions,
  mutations
}
